/**
 * Session provision tracking slice (GW-6192)
 *
 * Tracks which container session IDs have already had osintApi.launchSession
 * called for them, to prevent duplicate ECS task launches when the component
 * remounts (e.g. route key change, marketplace modal closing).
 *
 * Persisted in sessionStorage only (NOT localStorage) so that a real browser
 * navigation away clears the set, allowing legitimate re-launches.
 */

import type { StateCreator } from 'zustand';

const SESSION_PROVISION_STORAGE_KEY = 'gw-session-provision';

/**
 * Load provisioned sessions from sessionStorage
 */
function loadProvisionedSessions(): Set<string> {
  try {
    const stored = sessionStorage.getItem(SESSION_PROVISION_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return new Set(parsed);
    }
  } catch (error) {
    console.warn('[sessionProvisionSlice] Failed to load from sessionStorage:', error);
  }
  return new Set<string>();
}

/**
 * Save provisioned sessions to sessionStorage
 */
function saveProvisionedSessions(sessions: Set<string>): void {
  try {
    sessionStorage.setItem(SESSION_PROVISION_STORAGE_KEY, JSON.stringify(Array.from(sessions)));
  } catch (error) {
    console.warn('[sessionProvisionSlice] Failed to save to sessionStorage:', error);
  }
}

export interface SessionProvisionSlice {
  // State
  provisionedSessions: Set<string>;

  // Actions
  markProvisioned: (containerSessionId: string) => void;
  hasProvisioned: (containerSessionId: string) => boolean;
}

export const createSessionProvisionSlice: StateCreator<
  SessionProvisionSlice,
  [],
  [],
  SessionProvisionSlice
> = (set, get) => ({
  provisionedSessions: loadProvisionedSessions(),

  markProvisioned: (containerSessionId: string) => {
    const current = get().provisionedSessions;
    if (!current.has(containerSessionId)) {
      const updated = new Set(current).add(containerSessionId);
      set({ provisionedSessions: updated });
      saveProvisionedSessions(updated);
    }
  },

  hasProvisioned: (containerSessionId: string) => {
    return get().provisionedSessions.has(containerSessionId);
  },
});
